import { createPaginatedNetworkRequestHook } from '@/utils/createPaginatedNetworkRequestHook'

import { NetworkParams } from '@/lib/core/hooks/usePaginatedNetworkResource'
import { ListFilters } from '@/lib/core/interfaces/common.types'

import {
  AcceptConnectionRequestPayload,
  AcceptConnectionRequestResponse,
  CreateMyInvitationPayload,
  CreateMyInvitationResponse,
  Invitation,
  InvitationActionArgs,
  InvitationBase,
  MyInvitationsPayload,
  MyInvitationsResponse,
  RejectConnectionRequestPayload,
  VerifyConnectionRequestPayload,
  VerifyConnectionRequestResponse,
} from '@/lib/features/Contacts/interfaces'

import { request, useNetworkMutation } from '@/lib/utils/request'
import { makeServerSideRequest } from '@/lib/utils/serverSideRequest'

const baseUrl = 'v1/organizations/contacts'

export const makeApi = (instance = request) => ({
  acceptConnectionRequest: (data: AcceptConnectionRequestPayload) =>
    instance.post<AcceptConnectionRequestResponse>(
      `${baseUrl}/my/invitations/invitation_confirm/`,
      data,
    ),
  rejectConnectionRequest: (data: RejectConnectionRequestPayload) =>
    instance.post(`${baseUrl}/my/invitations/invitation_reject_confirm/`, data),
  verifyConnectionRequest: (data: VerifyConnectionRequestPayload) =>
    instance.post<VerifyConnectionRequestResponse>(
      `${baseUrl}/my/invitations/invitation_confirm_validate/`,
      data,
    ),
})

export const api = makeApi()
export const makeServerSideApi = (token?: string) => makeApi(makeServerSideRequest(token))
export const useMyContacts = createPaginatedNetworkRequestHook<
  Invitation,
  NetworkParams<ListFilters>,
  MyInvitationsResponse
>(`${baseUrl}/my/`)
export const useContactsSharedFromMe = createPaginatedNetworkRequestHook<InvitationBase>(
  `${baseUrl}/my/shared-from-me/`,
)
export const useContactsSharedToMe = createPaginatedNetworkRequestHook<InvitationBase>(
  `${baseUrl}/my/shared-to-me/`,
)

export const useMyInvitations = createPaginatedNetworkRequestHook<
  Invitation,
  NetworkParams<MyInvitationsPayload>,
  MyInvitationsResponse
>(`${baseUrl}/my/invitations/`)

export const useCreateInvitation = () =>
  useNetworkMutation<CreateMyInvitationResponse, CreateMyInvitationPayload>({
    url: `${baseUrl}/my/invitations/`,
    method: 'post',
  })

export const useAcceptInvitation = () =>
  useNetworkMutation<unknown, InvitationActionArgs>({
    url: `${baseUrl}/my/invitations/\${params.invitationId}/invitation_accept/`,
    method: 'post',
  })

export const useRejectInvitation = () =>
  useNetworkMutation<unknown, InvitationActionArgs>({
    url: `${baseUrl}/my/invitations/\${params.invitationId}/invitation_reject/`,
    method: 'post',
  })

export const useResendInvitation = () =>
  useNetworkMutation<unknown, InvitationActionArgs>({
    url: `${baseUrl}/my/invitations/\${params.invitationId}/resend_invitation/`,
    method: 'post',
  })

/** Cancels a pending request, or revokes an established connection. */
export const useDeleteInvitation = () =>
  useNetworkMutation<unknown, InvitationActionArgs>({
    url: `${baseUrl}/my/invitations/\${params.invitationId}/`,
    method: 'delete',
  })
